import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { TeachingTypeModel } from "../../models/subjects/subject.model";
import {
    MakeupTeachingDropdownOptionsResponse,
    MakeupTeachingDropdownOptionsParams,
    ScheduleSubmissionsTableModel,
    ScheduleSubmissionCorrectionRequestModel,
    ScheduleSubmissionCorrectionRequestPayload,
    ScheduleSubmissionBulkConfirmResponse,
    ScheduleSubmissionVersionDetailResponse,
    ScheduleSubmissionVersionListResponse,
    ScheduleSubmissionStatusHistoryParams,
    ScheduleSubmissionStatusHistoryResponse,
    ScheduleSubmissionReviewDecisionPayload,
    ScheduleSubmissionsListParams,
    ScheduleSubmissionsModel,
    ScheduleSubmissionProjectResearchDetailResponse,
    ScheduleSubmissionSubjectScheduleDetailResponse,
    ScheduleSubmissionsResponse
} from "../../models/schedule/schedule-submissions.model";


@Injectable({
    providedIn: "root",
})
export class ScheduleSubmissionsService {
    http = inject(HttpClient);
    private apiUrl = "/schedule_submissions";
    private teachingType = "/teaching_types";




    getTeachingType(): Observable<TeachingTypeModel[]> {
        return this.http.get<unknown>(this.teachingType)
            .pipe(map((response) => this.normalizeTeachingTypeResponse(response)));
    }

    getScheduleSubmissionsList(params?: ScheduleSubmissionsListParams): Observable<ScheduleSubmissionsResponse> {
        return this.http.get<unknown>(this.apiUrl, {
            params: this.buildListParams(params),
        }).pipe(
            map((res) => this.normalizeScheduleSubmissionsResponse(res, params))
        );
    }
    getMakeupTeachingDropdownOptions(
        scheduleSubmissionId: number,
        params?: MakeupTeachingDropdownOptionsParams
    ): Observable<MakeupTeachingDropdownOptionsResponse> {
        return this.http.get<MakeupTeachingDropdownOptionsResponse>(
            `${this.apiUrl}/${scheduleSubmissionId}/makeup_teaching_requests/dropdown_options`,
            { params: this.buildDropdownOptionsParams(params) }
        );
    }

    getScheduleSubmissionTabel(
        scheduleSubmissionId: number,
    ): Observable<ScheduleSubmissionsTableModel> {
        return this.http.get<ScheduleSubmissionsTableModel>(
            `${this.apiUrl}/${scheduleSubmissionId}`,
        );
    }

    getScheduleSubmissionVersions(
        scheduleSubmissionId: number,
    ): Observable<ScheduleSubmissionVersionListResponse> {
        return this.http.get<ScheduleSubmissionVersionListResponse>(
            `${this.apiUrl}/${scheduleSubmissionId}/versions`,
        );
    }

    getScheduleSubmissionVersion(
        scheduleSubmissionId: number,
        versionId: number,
    ): Observable<ScheduleSubmissionVersionDetailResponse> {
        return this.http.get<ScheduleSubmissionVersionDetailResponse>(
            `${this.apiUrl}/${scheduleSubmissionId}/versions/${versionId}`,
        );
    }

    getSubjectScheduleDetail(
        scheduleSubmissionId: number,
        subjectScheduleId: number,
    ): Observable<ScheduleSubmissionSubjectScheduleDetailResponse> {
        return this.http.get<ScheduleSubmissionSubjectScheduleDetailResponse>(
            `${this.apiUrl}/${scheduleSubmissionId}/subject_schedules/${subjectScheduleId}`,
        );
    }

    getProjectResearchSubjectDetail(
        scheduleSubmissionId: number,
        projectResearchSubjectId: number,
    ): Observable<ScheduleSubmissionProjectResearchDetailResponse> {
        return this.http.get<ScheduleSubmissionProjectResearchDetailResponse>(
            `${this.apiUrl}/${scheduleSubmissionId}/project_research_subjects/${projectResearchSubjectId}`,
        );
    }

    createCorrectionRequest(
        scheduleSubmissionId: number,
        payload: ScheduleSubmissionCorrectionRequestPayload
    ): Observable<ScheduleSubmissionCorrectionRequestModel> {
        return this.http.post<ScheduleSubmissionCorrectionRequestModel>(
            `${this.apiUrl}/${scheduleSubmissionId}/correction_requests`,
            payload
        );
    }

    getStatusHistory(
        scheduleSubmissionId: number,
        params?: ScheduleSubmissionStatusHistoryParams
    ): Observable<ScheduleSubmissionStatusHistoryResponse> {
        return this.http.get<ScheduleSubmissionStatusHistoryResponse>(
            `${this.apiUrl}/${scheduleSubmissionId}/status_history`,
            { params: this.buildStatusHistoryParams(params) }
        );
    }

    submitForTeacherReview(scheduleSubmissionId: number): Observable<ScheduleSubmissionsTableModel> {
        return this.http.post<ScheduleSubmissionsTableModel>(
            `${this.apiUrl}/${scheduleSubmissionId}/submit_for_teacher_review`,
            {}
        );
    }

    teacherConfirm(scheduleSubmissionId: number): Observable<ScheduleSubmissionsTableModel> {
        return this.http.post<ScheduleSubmissionsTableModel>(
            `${this.apiUrl}/${scheduleSubmissionId}/teacher_confirm`,
            {}
        );
    }

    educationHeadConfirm(scheduleSubmissionId: number): Observable<ScheduleSubmissionsTableModel> {
        return this.http.post<ScheduleSubmissionsTableModel>(
            `${this.apiUrl}/${scheduleSubmissionId}/education_head_confirm`,
            {}
        );
    }

    notifyRevisionRequested(scheduleSubmissionId: number): Observable<ScheduleSubmissionsTableModel> {
        return this.http.post<ScheduleSubmissionsTableModel>(
            `${this.apiUrl}/${scheduleSubmissionId}/notify_revision_requested`,
            {}
        );
    }

    bulkEducationHeadConfirm(scheduleSubmissionIds: number[]): Observable<ScheduleSubmissionBulkConfirmResponse> {
        return this.http.post<ScheduleSubmissionBulkConfirmResponse>(
            `${this.apiUrl}/bulk_education_head_confirm`,
            {
                schedule_submission_ids: scheduleSubmissionIds,
            }
        );
    }

    reviewAssignments(
        scheduleSubmissionId: number,
        decisions: ScheduleSubmissionReviewDecisionPayload[]
    ): Observable<ScheduleSubmissionsTableModel> {
        return this.http.post<ScheduleSubmissionsTableModel>(
            `${this.apiUrl}/${scheduleSubmissionId}/review_assignments`,
            {
                review: {
                    decisions,
                },
            }
        );
    }

    transitionApprovalRequest(
        approvalRequestId: number,
        payload: { to_status_code: string; actor_role_code?: string | null; comment?: string | null }
    ): Observable<unknown> {
        return this.http.post(`/approval_requests/${approvalRequestId}/transition`, {
            approval_request: payload,
        });
    }

    private buildDropdownOptionsParams(params?: MakeupTeachingDropdownOptionsParams): HttpParams {
        let httpParams = new HttpParams();

        const paramKeys: (keyof MakeupTeachingDropdownOptionsParams)[] = [
            'makeup_teaching_leave_type_id',
            'leave_date',
            'academic_holiday_id',
            'makeup_teaching_leave_period_id',
        ];

        paramKeys.forEach((key) => {
            httpParams = this.setParamIfPresent(httpParams, key, params?.[key]);
        });

        return httpParams;
    }
    private buildListParams(params?: ScheduleSubmissionsListParams): HttpParams {
        let httpParams = new HttpParams();

        const paramKeys: (keyof ScheduleSubmissionsListParams)[] = [
            'academic_term_id',
            'keywords',
            'department_id',
            'datatable',
            'only_approved',
            'user_id',
            'project_research_subject_id',
            'subject_schedule',
            'approval_review',
            'makeup_teaching_report',
            'page',
            'limit',
            'sort',
            'direction',
        ];

        paramKeys.forEach((key) => {
            httpParams = this.setParamIfPresent(httpParams, key, params?.[key]);
        });
        return httpParams;
    }

    private buildStatusHistoryParams(params?: ScheduleSubmissionStatusHistoryParams): HttpParams {
        let httpParams = new HttpParams();
        const paramKeys: (keyof ScheduleSubmissionStatusHistoryParams)[] = [
            'page',
            'limit',
            'keywords',
            'q',
            'sort_by',
            'sort_direction',
        ];

        paramKeys.forEach((key) => {
            httpParams = this.setParamIfPresent(httpParams, key, params?.[key]);
        });

        return httpParams;
    }

    private setParamIfPresent(
        httpParams: HttpParams,
        key: string,
        value: string | number | boolean | null | undefined
    ): HttpParams {
        if (value == null || value === '') {
            return httpParams;
        }

        return httpParams.set(key, String(value));
    }

    private normalizeTeachingTypeResponse(response: unknown): TeachingTypeModel[] {
        if (Array.isArray(response)) {
            return response as TeachingTypeModel[];
        }

        if (
            typeof response === 'object'
            && response !== null
            && Array.isArray((response as { data?: unknown }).data)
        ) {
            return (response as { data: TeachingTypeModel[] }).data;
        }

        return [];
    }

    private normalizeScheduleSubmissionsResponse(
        res: unknown,
        params?: ScheduleSubmissionsListParams
    ): ScheduleSubmissionsResponse {
        if (res !== null && typeof res === "object" && !Array.isArray(res)) {
            const response = res as Record<string, unknown>;

            if (Array.isArray(response.data)) {
                return {
                    data: response.data as ScheduleSubmissionsModel[],
                    page: Number(response.page ?? params?.page ?? 1),
                    limit: Number(response.limit ?? params?.limit ?? 10),
                    total: Number(response.total ?? response.data.length),
                    total_pages: Number(response.total_pages ?? 1),
                };
            }
        }

        const data = Array.isArray(res) ? res as ScheduleSubmissionsModel[] : [];
        const limit = params?.limit ?? 10;

        return {
            data,
            page: params?.page ?? 1,
            limit,
            total: data.length,
            total_pages: Math.max(1, Math.ceil(data.length / limit)),
        };
    }

}
